import React, { useEffect, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useLazyGetUsersQuery } from '@/store/api/userApi';
import { Badge } from '@/components/ui/badge';
import { BaseAvatar } from '@/components';
import { UserActionsDropdown } from '@/components/pages/SettingsPage/MainContent/shared/UserActionsDropdown';
import { useAppFilters } from '@/hooks';
import { filterByFields } from '@/components/pages/SettingsPage/MainContent/utils';

export const useUserManagementTable = () => {
  const { search: searchValue } = useAppFilters();
  const [fetchUsers, { data: usersData, isLoading: isLoadingUsersData, isFetching: isFetchingUsersData }] =
    useLazyGetUsersQuery();

  const tableData = useMemo(() => {
    if (!usersData) return [];
    if (!searchValue) return usersData;

    const fieldsToSearch = ['email', 'first_name', 'last_name', 'phone_number'];
    return filterByFields(usersData, searchValue, fieldsToSearch);
  }, [searchValue, usersData]);

  useEffect(() => {
    fetchUsers({}, true);
  }, []);

  const columns: ColumnDef<any>[] = useMemo(() => {
    return [
      {
        accessorFn: (row) => ({
          firstName: row.first_name,
          lastName: row.last_name,
          fullName: `${row.first_name} ${row.last_name}`,
        }),
        header: 'Full Name',
        cell: (info) => {
          const { firstName, lastName, fullName } = info.getValue() as {
            firstName: string;
            lastName: string;
            fullName: string;
          };
          const initials = `${firstName[0]}${lastName[0]}`;

          return (
            <div className="flex items-center gap-4">
              <BaseAvatar initials={initials} />
              <div>{fullName}</div>
            </div>
          );
        },
        enableFiltering: false,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: (info) => info.getValue(),
        enableFiltering: false,
      },
      {
        accessorKey: 'phone_number',
        header: 'Phone',
        cell: (info) => info.getValue(),
        enableFiltering: false,
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: (info: any) => {
          const roleMap: Record<string, string> = {
            dm_admin: 'DM Admin',
            admin: 'Admin',
            member: 'Member',
          };
          const displayValue = roleMap[info.getValue() as string] || info.getValue();
          return (
            <Badge variant="secondary" className="py-2 px-3 rounded-lg bg-[#E6E8EC]">
              {displayValue}
            </Badge>
          );
        },
        enableFiltering: false,
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }) => {
          const userId = row.original.user_id;
          return (
            <div className="flex items-center flex-row justify-end">
              <UserActionsDropdown userId={userId} />
            </div>
          );
        },
        enableSorting: false,
        enableFiltering: false,
        enableColumnFilter: false,
      },
    ];
  }, []);

  return {
    tableStructure: columns,
    tableData: tableData || [],
    isLoadingUsersData,
    isFetchingUsersData,
  };
};
